
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ArrowRight } from 'lucide-react';

// Données locales pour éviter les imports manquants
const departments = [
  { code: "12", name: "Aveyron", region: "Occitanie" },
  { code: "31", name: "Haute-Garonne", region: "Occitanie" },
  { code: "81", name: "Tarn", region: "Occitanie" },
  { code: "46", name: "Lot", region: "Occitanie" },
  { code: "82", name: "Tarn-et-Garonne", region: "Occitanie" },
  { code: "34", name: "Hérault", region: "Occitanie" },
  { code: "11", name: "Aude", region: "Occitanie" },
  { code: "15", name: "Cantal", region: "Auvergne-Rhône-Alpes" },
  { code: "63", name: "Puy-de-Dôme", region: "Auvergne-Rhône-Alpes" },
  { code: "48", name: "Lozère", region: "Occitanie" },
  { code: "95", name: "Val-d'Oise", region: "Île-de-France" }
];

const departmentSEOData: Record<string, { zones: string[] }> = {
  "12": { zones: ["Rodez", "Millau", "Decazeville", "Villefranche-de-Rouergue"] },
  "31": { zones: ["Toulouse", "Muret", "Colomiers"] },
  "81": { zones: ["Albi", "Castres", "Mazamet", "Carmaux"] },
  "46": { zones: ["Cahors", "Figeac", "Gourdon"] },
  "82": { zones: ["Montauban", "Castelsarrasin", "Moissac"] },
  "34": { zones: ["Montpellier", "Béziers", "Sète"] },
  "11": { zones: ["Carcassonne", "Castelnaudary", "Narbonne"] },
  "15": { zones: ["Aurillac", "Saint-Flour", "Mauriac"] },
  "63": { zones: ["Clermont-Ferrand", "Riom", "Thiers"] },
  "48": { zones: ["Mende", "Florac", "Marvejols"] },
  "95": { zones: ["Cergy", "Argenteuil", "Beauchamp", "Sarcelles"] }
};

const MAJOR_CITIES = [
  { name: "Rodez", deptCode: "12" },
  { name: "Millau", deptCode: "12" },
  { name: "Decazeville", deptCode: "12" },
  { name: "Figeac", deptCode: "46" },
  { name: "Albi", deptCode: "81" },
  { name: "Castres", deptCode: "81" },
  { name: "Mazamet", deptCode: "81" },
  { name: "Carmaux", deptCode: "81" },
  { name: "Beauchamp", deptCode: "95" },
  { name: "Toulouse", deptCode: "31" },
  { name: "Montpellier", deptCode: "34" },
  { name: "Castelnaudary", deptCode: "11" },
  { name: "Aurillac", deptCode: "15" },
  { name: "Clermont-Ferrand", deptCode: "63" },
];

const MetauxFerreuxSEO = () => {
  const groupedDepartments = departments.reduce((acc, dept) => {
    if (!acc[dept.region]) {
      acc[dept.region] = [];
    }
    acc[dept.region].push(dept);
    return acc;
  }, {} as Record<string, typeof departments>);

  return (
    <div className="max-w-4xl mx-auto px-4 mt-16">
      <div className="bg-fabre-lightbeige p-8 rounded-lg mb-12">
        <h2 className="text-2xl font-bold text-fabre-darkgreen mb-6">
          Recyclage des Métaux Ferreux par Département
        </h2>
        
        <p className="mb-6 text-gray-700">
          Le Groupe Fabre Environnement propose ses services de collecte, rachat et recyclage des métaux ferreux 
          dans de nombreux départements. Nos équipes d'experts sont à votre disposition pour tous vos besoins 
          en valorisation des déchets métalliques.
        </p>

        <div className="space-y-8">
          {Object.entries(groupedDepartments).map(([region, depts]) => (
            <div key={region} className="space-y-4">
              <h3 className="text-xl font-bold text-fabre-gray">{region}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {depts.map((dept) => (
                  <div key={dept.code} className="bg-white rounded-md p-4 shadow-sm border border-gray-200">
                    <h4 className="text-lg font-semibold text-fabre-gray">
                      {dept.name} ({dept.code})
                    </h4>
                    {departmentSEOData[dept.code] && (
                      <p className="text-gray-600 text-sm mt-2">
                        {departmentSEOData[dept.code].zones.slice(0, 3).join(', ')}, etc.
                      </p>
                    )}
                    <ul className="mt-3 space-y-1">
                      <li className="text-sm text-gray-700 flex items-center">
                        <ArrowRight className="h-3 w-3 text-fabre-green mr-2" />
                        Collecte des métaux ferreux
                      </li>
                      <li className="text-sm text-gray-700 flex items-center">
                        <ArrowRight className="h-3 w-3 text-fabre-green mr-2" />
                        Rachat au meilleur prix
                      </li>
                    </ul>
                    <div className="mt-4">
                      <Link 
                        to={`/nos-services/achat-metaux/${dept.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                        className="text-sm text-fabre-darkgreen hover:underline"
                      >
                        En savoir plus
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white p-8 rounded-lg shadow-md mb-12">
        <h2 className="text-2xl font-bold text-fabre-gray mb-6">
          Nos Services de Recyclage des Métaux Ferreux par Ville
        </h2>
        
        <p className="mb-6 text-gray-700">
          Le Groupe Fabre Environnement intervient dans les principales villes et communes pour 
          la collecte et le recyclage des métaux ferreux. Nos équipes locales assurent un service 
          de proximité adapté à vos besoins.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-6">
          {MAJOR_CITIES.map((city) => (
            <div key={city.name} className="text-center">
              <Link 
                to={`/nos-services/achat-metaux/${city.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                className="block p-4 rounded-md bg-fabre-lightgreen hover:bg-fabre-green/20 transition-colors"
              >
                <span className="text-fabre-darkgreen font-medium">{city.name}</span>
              </Link>
            </div>
          ))}
        </div>
        
        <div className="mt-8 text-center">
          <Button asChild className="bg-fabre-green hover:bg-fabre-darkgreen">
            <Link to="/contact">
              Demander un devis
            </Link>
          </Button>
        </div>
      </div>

      <div className="bg-gray-50 p-8 rounded-lg mb-12">
        <h2 className="text-2xl font-bold text-fabre-gray mb-6">
          Valorisation des Métaux Ferreux : Nos Engagements
        </h2>
        
        <p className="mb-6 text-gray-700">
          Chez Groupe Fabre Environnement, nous nous engageons à offrir:
        </p>
        
        <ul className="space-y-3">
          <li className="flex items-start">
            <ArrowRight className="h-5 w-5 text-fabre-green mr-3 mt-1" />
            <span>Des tarifs transparents et compétitifs pour le rachat de vos métaux ferreux</span>
          </li>
          <li className="flex items-start">
            <ArrowRight className="h-5 w-5 text-fabre-green mr-3 mt-1" />
            <span>Une traçabilité complète de vos déchets métalliques</span>
          </li>
          <li className="flex items-start">
            <ArrowRight className="h-5 w-5 text-fabre-green mr-3 mt-1" />
            <span>Des solutions logistiques adaptées à vos contraintes</span>
          </li>
          <li className="flex items-start">
            <ArrowRight className="h-5 w-5 text-fabre-green mr-3 mt-1" />
            <span>Un taux de valorisation optimal pour minimiser l'impact environnemental</span>
          </li>
        </ul>
        
        <div className="mt-8 text-center">
          <Button asChild variant="outline" className="border-fabre-green text-fabre-green hover:bg-fabre-lightgreen">
            <Link to="/engagements">
              Découvrir nos engagements
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MetauxFerreuxSEO;
